import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MediaPicker } from "@/components/ui/media-picker";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { PROBLEM_TAGS, PROBLEM_TAG_LABELS } from "@/lib/matching/taxonomy";
import {
  CaretUp,
  CaretDown,
  X,
  Plus,
  CircleNotch,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";

const MAX_TOPICS = 6;

interface TopicRow {
  tag: string;
  label: string;
  imageUrl: string;
  description: string | null;
}

interface TopicsResponse {
  topics: TopicRow[];
}

export function HomepageTopicsSection() {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [local, setLocal] = useState<TopicRow[] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "homepage-topics"],
    queryFn: async () => {
      const res = await fetch("/api/admin/homepage-topics");
      await throwIfNotOk(res, "Greška pri učitavanju tema");
      return res.json() as Promise<TopicsResponse>;
    },
  });

  const topics = local ?? data?.topics ?? [];
  const hasChanges = local !== null;
  const allHaveImages = topics.every((t) => t.imageUrl.trim().length > 0);

  const saveMutation = useMutation({
    mutationFn: async (rows: TopicRow[]) => {
      const res = await fetch("/api/admin/homepage-topics", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topics: rows.map((t) => ({
            tag: t.tag,
            imageUrl: t.imageUrl,
            description: t.description,
          })),
        }),
      });
      await throwIfNotOk(res, "Greška pri spremanju tema");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "homepage-topics"] });
      setLocal(null);
      toast.success("Teme na početnoj su ažurirane");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const update = (next: TopicRow[]) => setLocal(next);

  const swap = (a: number, b: number) => {
    const next = [...topics];
    const tmp = next[a]!;
    next[a] = next[b]!;
    next[b] = tmp;
    update(next);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    swap(index - 1, index);
  };

  const moveDown = (index: number) => {
    if (index >= topics.length - 1) return;
    swap(index, index + 1);
  };

  const removeTopic = (index: number) => {
    update(topics.filter((_, i) => i !== index));
  };

  const setImage = (index: number, imageUrl: string) => {
    update(topics.map((t, i) => (i === index ? { ...t, imageUrl } : t)));
  };

  const setDescription = (index: number, description: string) => {
    update(topics.map((t, i) => (i === index ? { ...t, description } : t)));
  };

  const addTopic = (tag: string) => {
    if (topics.length >= MAX_TOPICS) return;
    if (topics.some((t) => t.tag === tag)) return;
    update([
      ...topics,
      { tag, label: PROBLEM_TAG_LABELS[tag as keyof typeof PROBLEM_TAG_LABELS] ?? tag, imageUrl: "", description: null },
    ]);
    setPickerOpen(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-5 w-40 animate-pulse rounded bg-muted" />
          <div className="h-3.5 w-72 animate-pulse rounded bg-muted/60" />
        </div>
        <div className="grid gap-x-5 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 animate-pulse rounded bg-muted/60" />
              <div className="h-36 animate-pulse rounded-xl bg-muted/40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const usedTags = new Set(topics.map((t) => t.tag));
  const availableTags = PROBLEM_TAGS.filter((tag) => !usedTags.has(tag));
  const canAdd = topics.length < MAX_TOPICS && availableTags.length > 0;
  const missingImages = topics.filter((t) => !t.imageUrl.trim()).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h3
            className="text-base font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Teme na početnoj
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Redoslijed i slike odgovaraju onome što posjetilac vidi na
            početnoj.
          </p>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {topics.length}/{MAX_TOPICS}
        </span>
      </div>

      {/* Topic grid */}
      <div className="grid gap-x-5 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
        {topics.map((topic, index) => (
          <div key={topic.tag} className="group/topic">
            {/* Label row + controls */}
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs tabular-nums text-muted-foreground/70">
                {index + 1}
              </span>
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {topic.label}
              </p>
              <div className="flex shrink-0 items-center text-muted-foreground/50 opacity-0 transition-opacity group-hover/topic:opacity-100">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="rounded p-1 transition-colors hover:text-foreground disabled:opacity-30"
                  aria-label="Pomjeri ranije"
                >
                  <CaretUp className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index >= topics.length - 1}
                  className="rounded p-1 transition-colors hover:text-foreground disabled:opacity-30"
                  aria-label="Pomjeri kasnije"
                >
                  <CaretDown className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeTopic(index)}
                  className="rounded p-1 transition-colors hover:text-destructive"
                  aria-label="Ukloni temu"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Image */}
            <MediaPicker
              value={topic.imageUrl}
              onChange={(url) => setImage(index, url)}
              folder="homepage-topics"
              accept="image/jpeg,image/png,image/webp"
              hint="JPG, PNG ili WebP"
              previewHeight="h-36"
            />
            {!topic.imageUrl.trim() && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-600">
                <Warning className="size-3.5" />
                Slika je obavezna
              </p>
            )}

            {/* Description */}
            <Textarea
              value={topic.description ?? ""}
              onChange={(e) => setDescription(index, e.target.value)}
              placeholder="Kratak opis (opcionalno)"
              rows={2}
              className="mt-2 resize-none text-sm"
            />
          </div>
        ))}

        {/* Add tile */}
        {canAdd && (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="mt-7 flex min-h-36 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/70 text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
          >
            <Plus className="size-5" />
            <span className="text-sm">Dodaj temu</span>
          </button>
        )}
      </div>

      {topics.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Sekcija se neće prikazati na početnoj dok ne dodate barem jednu temu
          sa slikom.
        </p>
      )}
      {topics.length > 0 && missingImages > 0 && (
        <p className="text-xs text-amber-600">
          {missingImages === 1
            ? "Jedna tema nema sliku — dodajte je prije spremanja."
            : `${missingImages} teme nemaju sliku — dodajte je prije spremanja.`}
        </p>
      )}

      {/* Save bar */}
      <div className="sticky bottom-4 flex items-center justify-between gap-3 rounded-xl border bg-card/90 px-4 py-3 backdrop-blur-sm">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          {saveMutation.isSuccess && !hasChanges ? (
            <>
              <CheckCircle className="size-4 shrink-0 text-emerald-600" />
              <span className="truncate text-emerald-600">Spremljeno</span>
            </>
          ) : !allHaveImages && topics.length > 0 ? (
            <>
              <Warning className="size-4 shrink-0 text-amber-500" />
              <span className="truncate text-muted-foreground">Svaka tema mora imati sliku</span>
            </>
          ) : hasChanges ? (
            <span className="truncate text-muted-foreground">Nesačuvane promjene</span>
          ) : (
            <span className="truncate text-muted-foreground">Sve je ažurno</span>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={saveMutation.isPending || !hasChanges || !allHaveImages}
          onClick={() => saveMutation.mutate(topics)}
        >
          {saveMutation.isPending && <CircleNotch className="size-4 animate-spin" />}
          {saveMutation.isPending ? "Spremanje..." : "Spremi"}
        </Button>
      </div>

      <TopicPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        availableTags={availableTags}
        onAdd={addTopic}
      />
    </div>
  );
}

function TopicPickerDialog({
  open,
  onClose,
  availableTags,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  availableTags: readonly string[];
  onAdd: (tag: string) => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="sm:max-w-md">
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={Plus}
          title="Dodaj temu"
          description="Teme su fiksne i dolaze iz upitnika za uparivanje."
        />
      </DialogHeader>

      <DialogBody>
        {availableTags.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sve teme su već dodane.
          </p>
        ) : (
          <div className="-mx-1 flex flex-col">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onAdd(tag)}
                className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
              >
                <span className="truncate">
                  {PROBLEM_TAG_LABELS[tag as keyof typeof PROBLEM_TAG_LABELS] ?? tag}
                </span>
                <Plus className="size-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
